import { useMemo } from 'react';
import { reactHookForm } from '@yola/ws-ui';
import { i18next } from '@yola/ws-sdk';
import { elementTypeIds, elementStylesMap } from '../constants/element-customization';

const { useWatch } = reactHookForm;

const FONT_FAMILY_FIELD_NAMES = Object.values(elementTypeIds).map(
  (typeId) => `${typeId}${elementStylesMap.FONT_FAMILY}`
);

const getFontFamilyGroupOptions = ({
  currentFontFamilyFieldName,
  currentFontFamilyFieldValue,
  formValues,
  baseFontFamilyOptions,
}) => {
  const inUseFontFamilySet = new Set(
    FONT_FAMILY_FIELD_NAMES.map((fieldName) =>
      fieldName === currentFontFamilyFieldName ? currentFontFamilyFieldValue : formValues[fieldName]
    ).filter(Boolean)
  );

  const inUseFontFamilyOptions = [...inUseFontFamilySet].map((fontFamily) => ({
    value: fontFamily,
    label: fontFamily,
  }));

  const allFontFamilyOptions = baseFontFamilyOptions.filter(
    ({ value }) => !inUseFontFamilySet.has(value)
  );

  return { inUseFontFamilyOptions, allFontFamilyOptions };
};

const useFontFamilySelectOptions = ({ control, id, getValues, fontsData }) => {
  const currentFontFamilyFieldName = `${id}${elementStylesMap.FONT_FAMILY}`;
  const currentFontFamilyFieldValue = useWatch({ control, name: currentFontFamilyFieldName });
  const baseFontFamilyOptions = useMemo(
    () => fontsData.map(({ family }) => ({ value: family, label: family })),
    [fontsData]
  );

  return useMemo(() => {
    const { inUseFontFamilyOptions, allFontFamilyOptions } = getFontFamilyGroupOptions({
      currentFontFamilyFieldName,
      currentFontFamilyFieldValue,
      formValues: getValues(),
      baseFontFamilyOptions,
    });

    return [
      { label: i18next.t('In use'), options: inUseFontFamilyOptions },
      { label: i18next.t('All fonts'), options: allFontFamilyOptions },
    ];
  }, [currentFontFamilyFieldValue, getValues, currentFontFamilyFieldName, baseFontFamilyOptions]);
};

export default useFontFamilySelectOptions;
